export const version = '6.1.1';
export const navbarBreakPoint = 'xl'; // Vertical navbar breakpoint
export const topNavbarBreakpoint = 'lg';

/** User-selectable layout width for the main dashboard content shell. */
export const PAGE_CONTAINER_MODE = /** @type {const} */ ({
  FLUID: 'fluid',
  BOXED: 'boxed',
});

/**
 * Shared “chrome” for page wrappers: no decorative border/shadow (parent layout owns that).
 */
const transparentSurface = {
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 0,
  boxShadow: 'none',
};

/**
 * Layout presets for main content areas.
 *
 * - full: edge-to-edge (no padding) — embeds, special canvases
 * - fluid: full width with standard gutters (lists, simple details)
 * - boxed: centered column capped at maxWidthPx + gutters (invoice-style pages)
 * - boxedColumn: width cap + centering only; you supply padding (e.g. full-bleed header band)
 *
 * @param {number} maxWidthPx — inner max width for boxed variants
 */
export const createPageContainer = (maxWidthPx = 1800) => {
  const full = {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
    margin: '0 auto',
    padding: 0,
    ...transparentSurface,
  };

  const fluid = {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
    margin: '0 auto',
    marginInline: 0,
    paddingInline: 'var(--ant-padding-sm)',
    paddingBlock: 'var(--ant-padding-sm)',
    ...transparentSurface,
  };

  const boxedColumn = {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: maxWidthPx,
    marginInline: 'auto',
  };

  const boxed = {
    ...boxedColumn,
    paddingInline: 'var(--ant-padding-sm)',
    paddingBlock: 'var(--ant-padding-sm)',
  };

  return {
    maxWidthPx,
    styles: {
      full,
      fluid,
      boxed,
      boxedColumn,
    },
    /**
     * Optional overrides merged at runtime (e.g. per-route padding).
     * Example: { boxed: { paddingBlock: 0 } }
     */
    customStyles: {},
  };
};

// export const settings = {
//   isFluid: true,
//   ...
// };

export const settings = {
  isFluid: true,
  isRTL: false,
  isDark: false,
  developerMode: false,
  /** Main content width: full viewport gutters vs centered max-width column. */
  pageContainerMode: PAGE_CONTAINER_MODE.BOXED,
  pageContainer: createPageContainer(1680),
  // navbarPosition: 'combo', // top vertical combo
  showBurgerMenu: false, // controls showing vertical nav on mobile
  // currency: '$',
  isNavbarVerticalCollapsed: false, // toggle vertical navbar collapse
  navbarStyle: 'transparent', // transparent vibrant inverted card
  showNavbarSecondary: false,
  NavbarSecondaryContent: [],
  showFooterTools: false,
};

/** Old preset names still accepted by mergePageContainerStyle. */
const PAGE_CONTAINER_VARIANT_ALIASES = {
  fullWidth: 'fluid',
  constrained: 'boxed',
  constrainedColumn: 'boxedColumn',
};

/**
 * Merge a named pageContainer preset with inline overrides (customStyles entry wins last).
 * @param {'full'|'fluid'|'boxed'|'boxedColumn'|'fullWidth'|'constrained'|'constrainedColumn'} variant
 * @param {Record<string, unknown>} [overrides]
 */
export const mergePageContainerStyle = (variant, overrides = {}) => {
  const key = PAGE_CONTAINER_VARIANT_ALIASES[variant] || variant;
  const base = settings.pageContainer.styles[key];
  return {
    ...(base || {}),
    ...(settings.pageContainer.customStyles?.[key] ||
      settings.pageContainer.customStyles?.[variant] ||
      {}),
    ...overrides,
  };
};

/**
 * Styles for `AppPageContainer` (`.layout-container`): width / max-width / centering.
 * Horizontal gutters come from `_app-containers.scss` — do not set paddingInline here
 * or inline styles would override that stylesheet.
 *
 * @param {'fluid'|'boxed'} mode
 * @param {number} maxWidthPx — from `settings.pageContainer.maxWidthPx`
 */
export const getAppPageContainerShellStyle = (mode, maxWidthPx) => ({
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: mode === PAGE_CONTAINER_MODE.BOXED ? maxWidthPx : '100%',
  marginInline: mode === PAGE_CONTAINER_MODE.BOXED ? 'auto' : 0,
});

export default {
  version,
  navbarBreakPoint,
  topNavbarBreakpoint,
  settings,
  createPageContainer,
  mergePageContainerStyle,
  PAGE_CONTAINER_MODE,
  getAppPageContainerShellStyle,
};
